import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, User, ApiError } from '../../lib/api';
import Modal, { ConfirmModal } from '../../components/ui/Modal';
import { LoadingState, EmptyState } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import {
  createUserSchema,
  editUserSchema,
  resetPasswordSchema,
  type CreateUserFormData,
  type EditUserFormData,
  type ResetPasswordFormData,
} from '../../schemas/user';

type SortField = 'username' | 'fullName' | 'role' | 'isActive';
type SortDir = 'asc' | 'desc';

export default function UsersPage() {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [deactivateUser, setDeactivateUser] = useState<User | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('username');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.admin.users.list(),
  });

  const { data: entities } = useQuery({
    queryKey: ['admin-entities'],
    queryFn: () => api.admin.entities.list(),
  });

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    if (!data?.data) return [];

    let users = [...data.data];

    // Search filter
    if (search) {
      const s = search.toLowerCase();
      users = users.filter(u =>
        u.username.toLowerCase().includes(s) ||
        u.fullName.toLowerCase().includes(s) ||
        (u.email && u.email.toLowerCase().includes(s))
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      users = users.filter(u => u.role === roleFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      users = users.filter(u => u.isActive === isActive);
    }

    // Sort
    users.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'username') cmp = a.username.localeCompare(b.username);
      else if (sortField === 'fullName') cmp = a.fullName.localeCompare(b.fullName);
      else if (sortField === 'role') cmp = a.role.localeCompare(b.role);
      else if (sortField === 'isActive') cmp = (a.isActive ? 1 : 0) - (b.isActive ? 1 : 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return users;
  }, [data?.data, search, roleFilter, statusFilter, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => (
    <span className="ml-1 text-[10px] opacity-50 group-hover:opacity-100">
      {sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : '○'}
    </span>
  );

  const deactivateMutation = useMutation({
    mutationFn: (userId: string) =>
      fetch(`/api/admin/users/${userId}`, { method: 'DELETE', credentials: 'include' }).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      success(t('users.messages.userDeactivated'));
      setDeactivateUser(null);
    },
    onError: () => showError('Failed to deactivate user'),
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('users.title')}</h1>
        <button className="btn btn-primary" onClick={() => { setEditingUser(null); setShowForm(true); }}>
          {t('users.addUser')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          className="form-input !w-auto min-w-[250px]"
          placeholder={t('users.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-input !w-auto min-w-[180px]"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">{t('users.filters.allRoles')}</option>
          <option value="admin">{t('users.roles.admin')}</option>
          <option value="accountant">{t('users.roles.accountant')}</option>
          <option value="cashier">{t('users.roles.cashier')}</option>
          <option value="viewer">{t('users.roles.viewer')}</option>
        </select>
        <select
          className="form-input !w-auto min-w-[180px]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">{t('users.filters.allStatus')}</option>
          <option value="active">{t('users.status.active')}</option>
          <option value="inactive">{t('users.status.inactive')}</option>
        </select>
        <span className="ml-auto text-sm text-foreground-muted">{t('users.usersCount', { count: filteredUsers.length })}</span>
      </div>

      {isLoading ? (
        <LoadingState message={t('users.loadingUsers')} />
      ) : filteredUsers.length === 0 ? (
        <div className="card">
          <EmptyState
            title={data?.data.length === 0 ? t('users.noUsers') : t('users.noMatchingUsers')}
            description={data?.data.length === 0 ? t('users.noUsersDescription') : t('users.noMatchingDescription')}
          />
        </div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th className="cursor-pointer select-none group hover:bg-background-secondary" onClick={() => handleSort('username')}>
                  {t('users.fields.username')} <SortIcon field="username" />
                </th>
                <th className="cursor-pointer select-none group hover:bg-background-secondary" onClick={() => handleSort('fullName')}>
                  {t('users.fields.fullName')} <SortIcon field="fullName" />
                </th>
                <th className="cursor-pointer select-none group hover:bg-background-secondary" onClick={() => handleSort('role')}>
                  {t('users.fields.role')} <SortIcon field="role" />
                </th>
                <th>{t('users.fields.entities')}</th>
                <th className="cursor-pointer select-none group hover:bg-background-secondary" onClick={() => handleSort('isActive')}>
                  {t('users.fields.status')} <SortIcon field="isActive" />
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className={!user.isActive ? 'opacity-60' : ''}>
                  <td><strong>{user.username}</strong></td>
                  <td>{user.fullName}</td>
                  <td><span className={`badge badge-${user.role}`}>{t(`users.roles.${user.role}`)}</span></td>
                  <td className="text-muted">{user.entities?.map(e => e.code).join(', ') || '-'}</td>
                  <td>
                    <span className={`badge ${user.isActive ? 'badge-closed' : 'badge-open'}`}>
                      {user.isActive ? t('users.status.active') : t('users.status.inactive')}
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="flex gap-2 justify-end">
                      <button
                        className="btn btn-secondary px-3 py-1 text-[13px]"
                        onClick={() => { setEditingUser(user); setShowForm(true); }}
                      >
                        {t('users.actions.edit')}
                      </button>
                      <button
                        className="btn btn-secondary px-3 py-1 text-[13px]"
                        onClick={() => setResetPasswordUser(user)}
                      >
                        {t('users.actions.resetPassword')}
                      </button>
                      {user.isActive && (
                        <button
                          className="btn btn-danger px-3 py-1 text-[13px]"
                          onClick={() => setDeactivateUser(user)}
                        >
                          {t('users.actions.deactivate')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <UserFormModal
          user={editingUser}
          entities={entities?.data || []}
          onClose={() => { setShowForm(false); setEditingUser(null); }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            setShowForm(false);
            setEditingUser(null);
            success(editingUser ? t('users.messages.userUpdated') : t('users.messages.userCreated'));
          }}
        />
      )}

      {resetPasswordUser && (
        <ResetPasswordModal
          user={resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
          onSuccess={() => {
            setResetPasswordUser(null);
            success(t('users.messages.passwordReset'));
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!deactivateUser}
        onClose={() => setDeactivateUser(null)}
        onConfirm={() => deactivateUser && deactivateMutation.mutate(deactivateUser.id)}
        title={t('users.deactivateTitle')}
        message={t('users.deactivateMessage', { name: deactivateUser?.fullName })}
        confirmText={t('users.actions.deactivate')}
        confirmVariant="danger"
        isLoading={deactivateMutation.isPending}
      />

    </div>
  );
}

function UserFormModal({
  user,
  entities,
  onClose,
  onSuccess,
}: {
  user: User | null;
  entities: any[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation('admin');
  const { error: showError } = useToast();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateUserFormData | EditUserFormData>({
     
    resolver: zodResolver((user ? editUserSchema : createUserSchema) as any),
    defaultValues: user
      ? {
          fullName: user.fullName,
          email: user.email || '',
          role: user.role as 'admin' | 'accountant' | 'cashier' | 'viewer',
          entityIds: user.entities?.map(e => e.id) || [],
          isActive: String(user.isActive) as unknown as boolean,
        }
      : {
          username: '',
          password: '',
          fullName: '',
          email: '',
          role: 'cashier',
          entityIds: [],
        },
  });

  const selectedEntityIds = watch('entityIds') || [];

  const createMutation = useMutation({
    mutationFn: (data: any) => api.admin.users.create(data),
    onSuccess,
    onError: (err: ApiError) => {
      showError(err.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => api.admin.users.update(user!.id, data),
    onSuccess,
    onError: (err: ApiError) => {
      showError(err.message);
    },
  });

  const onSubmit = (data: CreateUserFormData | EditUserFormData) => {
    if (user) {
      const editData = data as EditUserFormData;
      updateMutation.mutate({
        fullName: editData.fullName,
        email: editData.email || null,
        role: editData.role,
        entityIds: editData.entityIds,
        isActive: editData.isActive,
      });
    } else {
      const createData = data as CreateUserFormData;
      createMutation.mutate({
        username: createData.username,
        password: createData.password,
        fullName: createData.fullName,
        email: createData.email || undefined,
        role: createData.role,
        entityIds: createData.entityIds,
      });
    }
  };

  const handleEntityChange = (entityId: string, checked: boolean) => {
    const current = selectedEntityIds;
    if (checked) {
      setValue('entityIds', [...current, entityId]);
    } else {
      setValue('entityIds', current.filter(id => id !== entityId));
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal isOpen={true} onClose={onClose} title={user ? t('users.editUser') : t('users.newUser')} size="md">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label className="form-label">{t('users.fields.username')}</label>
          {user ? (
            <input
              type="text"
              className="form-input"
              value={user.username}
              disabled
            />
          ) : (
            <>
              <input
                type="text"
                className={`form-input ${(errors as any).username ? 'border-error' : ''}`}
                {...register('username' as any)}
              />
              {(errors as any).username && (
                <span className="block text-error text-[13px] mt-1">{(errors as any).username?.message}</span>
              )}
            </>
          )}
        </div>

        {!user && (
          <div className="form-group">
            <label className="form-label">{t('users.fields.password')}</label>
            <div className="relative flex items-center">
              <input
                type={showPassword ? 'text' : 'password'}
                className={`form-input pr-11 ${(errors as any).password ? 'border-error' : ''}`}
                {...register('password' as any)}
              />
              <button
                type="button"
                className="absolute right-2 bg-transparent border-none p-1 cursor-pointer text-foreground-muted hover:text-foreground flex items-center justify-center rounded transition-colors"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {(errors as any).password && (
              <span className="block text-error text-[13px] mt-1">{(errors as any).password?.message}</span>
            )}
            <small className="text-muted">{t('users.passwordHint')}</small>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{t('users.fields.fullName')}</label>
            <input
              type="text"
              className={`form-input ${errors.fullName ? 'border-error' : ''}`}
              {...register('fullName')}
            />
            {errors.fullName && (
              <span className="block text-error text-[13px] mt-1">{errors.fullName.message}</span>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">{t('users.fields.emailOptional')}</label>
            <input
              type="email"
              className={`form-input ${errors.email ? 'border-error' : ''}`}
              {...register('email')}
            />
            {errors.email && (
              <span className="block text-error text-[13px] mt-1">{errors.email.message}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{t('users.fields.role')}</label>
            <select className="form-input" {...register('role')}>
              <option value="admin">{t('users.roleDescriptions.admin')}</option>
              <option value="accountant">{t('users.roleDescriptions.accountant')}</option>
              <option value="cashier">{t('users.roleDescriptions.cashier')}</option>
              <option value="viewer">{t('users.roleDescriptions.viewer')}</option>
            </select>
          </div>
          {user && (
            <div className="form-group">
              <label className="form-label">{t('users.fields.status')}</label>
              <select className="form-input" {...register('isActive')}>
                <option value="true">{t('users.status.active')}</option>
                <option value="false">{t('users.status.inactive')}</option>
              </select>
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('users.fields.entities')}</label>
          <div className={`flex flex-col gap-2 p-3 bg-background rounded border ${errors.entityIds ? 'border-error' : 'border-transparent'}`}>
            {entities.map((entity) => (
              <label key={entity.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedEntityIds.includes(entity.id)}
                  onChange={(e) => handleEntityChange(entity.id, e.target.checked)}
                />
                <span>{entity.code} - {entity.name}</span>
              </label>
            ))}
          </div>
          {errors.entityIds && (
            <span className="block text-error text-[13px] mt-1">{errors.entityIds.message}</span>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('users.actions.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={isPending}>
            {isPending ? t('users.actions.saving') : t('users.actions.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onSuccess,
}: {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation('admin');
  const { error: showError } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const mutation = useMutation({
    mutationFn: (password: string) => api.admin.users.setPassword(user.id, password),
    onSuccess,
    onError: (err: ApiError) => {
      showError(err.message);
    },
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    mutation.mutate(data.password);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={t('users.actions.resetPassword')} size="sm">
      <form onSubmit={handleSubmit(onSubmit)}>
        <p className="text-muted mb-4">
          {t('users.resetPasswordFor')} <strong>{user.fullName}</strong>
        </p>

        <div className="form-group">
          <label className="form-label">{t('users.fields.newPassword')}</label>
          <div className="relative flex items-center">
            <input
              type={showPassword ? 'text' : 'password'}
              className={`form-input pr-11 ${errors.password ? 'border-error' : ''}`}
              {...register('password')}
            />
            <button
              type="button"
              className="absolute right-2 bg-transparent border-none p-1 cursor-pointer text-foreground-muted hover:text-foreground flex items-center justify-center rounded transition-colors"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password && (
            <span className="block text-error text-[13px] mt-1">{errors.password.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('users.fields.confirmPassword')}</label>
          <div className="relative flex items-center">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              className={`form-input pr-11 ${errors.confirmPassword ? 'border-error' : ''}`}
              {...register('confirmPassword')}
            />
            <button
              type="button"
              className="absolute right-2 bg-transparent border-none p-1 cursor-pointer text-foreground-muted hover:text-foreground flex items-center justify-center rounded transition-colors"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              tabIndex={-1}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <span className="block text-error text-[13px] mt-1">{errors.confirmPassword.message}</span>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('users.actions.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? t('users.actions.resetting') : t('users.actions.resetPassword')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
